<template>
  <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-900/5 tw-p-4">
    <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
      <div class="tw-flex tw-items-center tw-gap-2">
        <font-awesome-icon icon="location-dot" class="tw-h-4 tw-w-4 tw-text-slate-500" />
        <h4 class="tw-text-base tw-font-semibold tw-text-slate-900">{{ receiverText }}</h4>
        <CopyButton
          v-if="receiverCopyValue"
          :value="receiverCopyValue"
          title="Copy location"
          ariaLabel="Copy location"
        />
      </div>

      <button
        v-if="editable"
        type="button"
        class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
        @click="toggleEditingCustomer"
        title="Edit"
      >
        <font-awesome-icon icon="pen-to-square" />
      </button>
    </div>

    <div v-if="order.receiver" class="tw-mt-3">
      <div v-if="!isEditingCustomer" class="tw-text-sm tw-text-slate-700">
        <div class="tw-font-medium tw-text-slate-900">{{ order.receiver.name }}</div>
        <div
          v-if="
            order.receiver.deliveryAddress &&
            order.receiver.deliveryAddress.streetAddress
          "
        >
          {{ addressLine2(order.receiver.deliveryAddress) }}
        </div>
        <div>{{ addressLine3(order.receiver.deliveryAddress) }}</div>
      </div>

        <div v-if="isEditingCustomer" class="uk-flex uk-width-1-1">
          <EditCustomer
            :orderId="order.id"
            :currentCustomerId="order.receiver && order.receiver.id"
            :currentCustomer="order.receiver"
            @saveCustomer="saveCustomer"
            @cancelCustomerEdit="cancelEditingCustomer"
          >
          </EditCustomer>
        </div>

        <div v-else class="uk-flex uk-width-1-1"></div>

        <div v-if="showReceivingContactDropdown" class="tw-mt-3 tw-w-full sm:tw-max-w-sm">
          <div
            class="tw-flex tw-h-10 tw-w-full tw-items-center tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-backdrop-blur"
          >
            <div class="tw-min-w-0 tw-flex-1">
              <filterable-dropdown
                class="tw-w-full tw-min-w-0"
                :options="order.receiver.contacts"
                label="name"
                :inPill="true"
                @input="setReceivingContact"
              ></filterable-dropdown>
            </div>
            <div class="tw-my-2 tw-w-px tw-bg-slate-200/70"></div>
            <button
              type="button"
              class="tw-inline-flex tw-h-10 tw-shrink-0 tw-items-center tw-justify-center tw-px-3 tw-text-slate-700 hover:tw-bg-white"
              title="Cancel"
              aria-label="Cancel"
              @click="toggleAddReceivingContact"
            >
              <font-awesome-icon icon="xmark" />
            </button>
          </div>
        </div>
        <div v-else-if="order.receivingContact">
          <p
            class="uk-margin-remove-left uk-margin-remove-bottom uk-margin-top"
          >
            {{ order.receivingContact.name }}
          </p>
          <p class="uk-margin-remove" v-if="order.receivingContact.email">
            {{ order.receivingContact.email }}
          </p>
          <p class="uk-margin-remove" v-if="order.receivingContact.phoneNumber">
            {{ order.receivingContact.phoneNumber }}
          </p>
        </div>
        <button
          v-else-if="!isEditingCustomer && showEditContact"
          type="button"
          class="tw-mt-3 tw-inline-flex tw-h-10 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-px-3 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
          @click="toggleAddReceivingContact"
          title="Add contact"
          aria-label="Add contact"
        >
          <font-awesome-icon icon="plus" />
          <span>Add Contact</span>
        </button>
        <Button
          v-if="!isEditingCustomer && showDetails"
          @click="showCustomerDetails"
          :isDefault="true"
          class="uk-margin-small-top uk-margin-small-left"
        >Details</Button>
      </div>
  </div>
  <CustomerDetails
    v-if="isCustomerDetailsOpen"
    v-model:isOpen="isCustomerDetailsOpen"
    :customerId="order?.receiver?.id"
  />
</template>


<script lang="ts">
import { defineComponent } from 'vue';
import UpdateReceivingContactMutation from '@/gql/orders/mutations/UpdateReceivingContact.gql';

import CustomerDetails from '../../Customer/CustomerDetails.vue';
import Button from '../../core/Button.vue';
import EditCustomer from './EditCustomer.vue';
import FilterableDropdown from '../../core/FilterableDropdown.vue';
import { addressMixin } from '../../../utils/address';

import CopyButton from '@/components/core/CopyButton.vue';

export default defineComponent({
  data() {
    return {
      isAddingReceivingContact: false,
      originCustomerNameQuery: '',
      isEditingCustomer: false,
      isCustomerDetailsOpen: false,
    };
  },
  props: {
    order: {
      type: Object,
    },
    editable: {
      default: true,
    },
    showDetails: {
      default: true,
    },
    showEditContact: {
      default: true,
    },
  },
  mixins: [addressMixin],
  components: {
    Button,
    EditCustomer,
    FilterableDropdown,
    CopyButton,
    CustomerDetails,
  },
  computed: {
    receiverText(): string {
      const vm = (this as any);

      if (vm.order?.isExport) {
        return 'Pick-Up Location';
      }

      if (
        vm.order.orderType.includes('Import') ||
        vm.order.orderType.includes('RailPick')
      ) {
        return 'Drop-Off Location';
      } else {
        return 'Pick-Up Location';
      }
    },
    isExportOrder(): boolean {
      const vm = (this as any);

      if (!vm.order) {
        return false;
      }
      return vm.order.isExport;
    },
    showReceivingContactDropdown(): boolean {
      const vm = (this as any);

      const isEditingCustomer = vm.isEditingCustomer;
      const isAddingReceivingContact = vm.isAddingReceivingContact;
      return !isEditingCustomer && isAddingReceivingContact;
    },
    receiverCopyValue(): string {
      const vm = this as any;
      const receiver = vm.order?.receiver;
      if (!receiver) return '';

      const name = receiver.name || '';
      const line2 = receiver.deliveryAddress ? vm.addressLine2(receiver.deliveryAddress) : '';
      const line3 = receiver.deliveryAddress ? vm.addressLine3(receiver.deliveryAddress) : '';

      return [name, line2, line3].filter(Boolean).join('\n');
    },
  },
  methods: {
    saveCustomer() {
      const vm = (this as any);

      vm.$emit('updateOrder');
      vm.isAddingReceivingContact = false;
      vm.isEditingCustomer = false;
    },
    cancelEditingCustomer() {
      (this as any).isEditingCustomer = false;
    },
    onOriginCustomerSearch(customerName) {
      console.log('custome change');
      (this as any).originCustomerNameQuery = customerName;
    },
    showCustomerDetails() {
      const vm = (this as any);
      const customer = vm.order?.receiver;
      if (!customer || customer.id === undefined || customer.id === null) {
        window.location.href = '/customers';
        return;
      }
      vm.isCustomerDetailsOpen = true
    },
    toggleAddReceivingContact() {
      const vm = (this as any);

      if (!vm.isAddingReceivingContact) {
        vm.isAddingReceivingContact = true;
      } else {
        vm.isAddingReceivingContact = false;
      }
    },

    toggleEditingCustomer() {
      const vm = (this as any);

      if (!vm.isEditingCustomer) {
        vm.isEditingCustomer = true;
      } else {
        vm.isEditingCustomer = false;
      }
    },
    setReceivingContact(contact) {
      const vm = (this as any);

      vm.$apollo
        .mutate({
          mutation: UpdateReceivingContactMutation,
          variables: {
            orderId: vm.order.id,
            contactId: contact.id,
          },
        })
        .then((data) => {
          vm.isAddingReceivingContact = false;
        })
        .catch((err) => {
          console.error(err);
        });
    },
  },
});
</script>